import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Save, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

export default function WhatsAppSettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .in('setting_key', [
        'whatsapp_enabled',
        'whatsapp_api_key',
        'whatsapp_phone_number',
        'whatsapp_account_sid'
      ]);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: any) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le configurazioni WhatsApp sono state aggiornate',
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/settings')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurazione WhatsApp</h1>
              <p className="text-gray-600 mt-1">Integra notifiche WhatsApp Business</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>WhatsApp Business API</CardTitle>
            <CardDescription>
              Configura l'integrazione con WhatsApp Business per inviare notifiche
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Abilita notifiche WhatsApp</Label>
                <p className="text-sm text-gray-500">
                  Attiva l'invio di notifiche via WhatsApp
                </p>
              </div>
              <Switch
                checked={settings['whatsapp_enabled'] === 'true'}
                onCheckedChange={(checked) =>
                  handleSettingChange('whatsapp_enabled', checked ? 'true' : 'false')
                }
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="whatsapp_phone_number">Numero WhatsApp Business</Label>
              <Input
                id="whatsapp_phone_number"
                type="tel"
                value={settings['whatsapp_phone_number'] || ''}
                onChange={(e) => handleSettingChange('whatsapp_phone_number', e.target.value)}
                placeholder="+39 XXX XXX XXXX"
              />
              <p className="text-xs text-gray-500">
                Formato internazionale con prefisso (es. +39 per Italia)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_account_sid">Twilio Account SID</Label>
              <div className="relative">
                <Input
                  id="whatsapp_account_sid"
                  type={showSecrets['whatsapp_account_sid'] ? 'text' : 'password'}
                  value={settings['whatsapp_account_sid'] || ''}
                  onChange={(e) => handleSettingChange('whatsapp_account_sid', e.target.value)}
                  placeholder="Account SID di Twilio"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('whatsapp_account_sid')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['whatsapp_account_sid'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_api_key">API Key</Label>
              <div className="relative">
                <Input
                  id="whatsapp_api_key"
                  type={showSecrets['whatsapp_api_key'] ? 'text' : 'password'}
                  value={settings['whatsapp_api_key'] || ''}
                  onChange={(e) => handleSettingChange('whatsapp_api_key', e.target.value)}
                  placeholder="API Key WhatsApp Business"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('whatsapp_api_key')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['whatsapp_api_key'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Come configurare WhatsApp Business</h3>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>Registrati per un account WhatsApp Business API</li>
                  <li>Verifica il tuo numero di telefono aziendale</li>
                  <li>Ottieni le credenziali API dal provider (es. Twilio, Meta)</li>
                  <li>Crea i template di messaggio approvati</li>
                  <li>Inserisci qui le credenziali API</li>
                </ol>
                <p className="text-sm text-gray-700 mt-3">
                  <strong>Nota:</strong> WhatsApp Business API richiede l'approvazione di Meta e l'uso di template pre-approvati per le notifiche.
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
